import React, { useEffect, useState } from "react";
import { Paragraph } from "../../Commons/Paragraph";
import { Colors } from "../../Commons/Colors/Colors";
import ResearchTable from "../ResearchTable/ResearchTable";
import {
  commonHeaders,
  headerAccordingPlayerPosition,
  passingHeaders,
  receivingHeaders,
  rushingHeaders,
} from "../../../lib/utils";
import TableTabs from "../../Commons/TableTabs/TableTabs";
import ArrowDown from "../../../assets/imgs/arrow-right.svg";

interface StatsItem {
  value: string | number;
  rank: string | null;
}

interface StatsData {
  [key: string]: StatsItem;
}

interface Props {
  statsData: StatsData | null;
  weeklyStats: any[] | null;
  playerPosition: string;
}

const ResearchStats: React.FC<Props> = ({
  statsData,
  weeklyStats,
  playerPosition,
}) => {
  const [tabsOpts] = useState(headerAccordingPlayerPosition(playerPosition));
  const [customHeaders, setCustomHeaders] = useState(commonHeaders);
  const [expandedSeasons, setExpandedSeasons] = useState<{
    [key: string]: boolean;
  }>({});
  const [activeTabs, setActiveTabs] = useState<{ [key: string]: string }>({});

  // Add useEffect to automatically expand the latest season
  useEffect(() => {
    if (weeklyStats && weeklyStats.length > 0) {
      // Find the latest season (year) from the sorted data
      const sortedYears = [...weeklyStats].sort(
        (a, b) => Number(Object.keys(b)[0]) - Number(Object.keys(a)[0])
      );
      const latestSeason = Object.keys(sortedYears[0])[0];
      
      // Set the latest season to expanded
      setExpandedSeasons(prev => ({
        ...prev,
        [latestSeason]: true
      }));
    }
  }, [weeklyStats]);

  if (!statsData && !weeklyStats) {
    return (
      <Paragraph
        text="-- No Data --"
        props={{ 
          fontSize: "22px", 
          fontWeight: 500,
          display: "flex", 
          justifyContent: "center",
          opacity: 0.5 
        }}
        className="ml-5"
        ParagraphType="Pragragh22"
        color="textPrimary"
      />
    );
  }

  const getStatsCategoryByPosition = (position: string): string[] => {
    switch (position.toUpperCase()) {
      case "QB":
        return ["passing", "rushing"];
      case "RB":
        return ["rushing", "receiving"];
      case "WR":
        return ["receiving", "rushing"];
      case "TE":
        return ["receiving", "rushing"];
      default:
        return ["receiving", "rushing"];
    }
  };

  const statsCategory = getStatsCategoryByPosition(playerPosition);

  const headersMap: { [key: string]: any[] } = {
    FANTASY: [...commonHeaders],
    PASSING: [...commonHeaders, ...passingHeaders],
    RUSHING: [...commonHeaders, ...rushingHeaders],
    RECEIVING: [...commonHeaders, ...receivingHeaders],
  };

  const handleSelectPosition = (season: string, tabName: string) => {
    setActiveTabs((prev) => ({ ...prev, [season]: tabName }));
    setCustomHeaders(
      headersMap[tabName] || [...commonHeaders, ...receivingHeaders],
    );
  };

  const toggleSeason = (season: string) => {
    setExpandedSeasons((prev) => ({ ...prev, [season]: !prev[season] }));
  };

  return (
    <div>
      {weeklyStats &&
        [...weeklyStats]
          .sort((a, b) => Number(Object.keys(b)[0]) - Number(Object.keys(a)[0]))
          .map((yearData, index) => {
            const season = Object.keys(yearData)[0];
            const activeTab = activeTabs[season] || tabsOpts[0]?.name;
            const customHeaders = headersMap[activeTab] || commonHeaders;

            return (
              <div key={index}>
                <div
                  className="w-full text-left py-2 px-5 text-white flex items-center text-[12px] font-semibold gap-3"
                  onClick={() => toggleSeason(season)}
                >
                  {season} SEASON
                  <img
                    src={ArrowDown}
                    alt=""
                    className={`${
                      expandedSeasons[season]
                        ? "rotate-[-90deg]"
                        : "rotate-[90deg]"
                    } transition-all duration-300 ease-in-out w-4`}
                  />
                </div>
                {expandedSeasons[season] && (
                  <div className="mt-4">
                    <Paragraph
                      text={"REGULAR SEASON"}
                      props={{
                        fontSize: "10px",
                        fontWeight: 400,
                        lineHeight: "100%",
                      }}
                      ParagraphType="Pragragh22"
                      color="textPrimary"
                      className="ml-5 -mt-4 mb-2"
                    />
                    {statsData && (
                      <div className="flex items-center gap-[10px] overflow-x-auto no-scrollbar -mx-5 px-5">
                        {Object.entries(statsData).map(([key, data], index) => (
                          <div
                            key={index}
                            className="min-w-[112px] rounded-xl p-4 flex flex-col items-center justify-between bg-gray-700"
                          >
                            <Paragraph
                              text={data.value.toString()}
                              props={{
                                fontSize: "34px",
                                fontWeight: 500,
                                lineHeight: "85%",
                              }}
                              ParagraphType="Pragragh22"
                              color="textPrimary"
                            />
                            <div className="text-center">
                              <Paragraph
                                text={data.rank ? data.rank : ""}
                                props={{
                                  fontSize: "12px",
                                  lineHeight: "130%",
                                  marginTop: "20px",
                                }}
                                ParagraphType="Pragragh22"
                                color="textPrimary"
                              />
                              <Paragraph
                                text={key}
                                props={{
                                  fontSize: "10px",
                                  lineHeight: "130%",
                                  color: Colors.textLigth,
                                  marginTop:
                                    data.rank === "" || !data.rank
                                      ? "38px"
                                      : "",
                                }}
                                ParagraphType="Pragragh22"
                                color="textPrimary"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="px-5 pt-3 pb-4">
                      <TableTabs
                        tabItems={tabsOpts}
                        tabCustomStyle="h-[25.25px] rounded-[23.29px] text-[10.35px]  shrink-0 px-3 py-0 w-fit"
                        onTabSelect={(item) =>
                          handleSelectPosition(season, item.name)
                        }
                        resetTabs={false}
                        selectedTabColor="#08262A"
                        unSelectTabColor="#1C1C1A"
                        selectedTabStyle="!text-[#00E0FF]"

                      />
                    </div>
                    <div className="overflow-x-hidden pb-5">
                      <ResearchTable
                        tableHeader={customHeaders}
                        data={yearData[season]}
                        statsCategory={statsCategory}
                        positions={playerPosition}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
    </div>
  );
};

export default ResearchStats;
